import { useState, FormEvent } from "react";
import Toast from "@/components/base/Toast";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { lean, site } from "@/content";
import { Rich } from "@/lib/rich";

export default function LeanContact() {
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [toast, setToast] = useState({
    message: "",
    type: "success" as "success" | "error",
    visible: false,
  });
  const phoneMask = usePhoneMask();
  const { ref, revealed } = useScrollReveal();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      setToast({ message: lean.contact.form.errors.tooLong, type: "error", visible: true });
      return;
    }
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    data.append("page_path", window.location.pathname);
    data.append("form_name", "lean-contact-form");
    try {
      await fetch(site.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setToast({ message: lean.contact.form.errors.success, type: "success", visible: true });
      form.reset();
      setCharCount(0);
    } catch {
      setToast({ message: lean.contact.form.errors.generic, type: "error", visible: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#FAFAF8] to-white dark:from-[#0A0A0A] dark:to-[#0C0C0E]">
      <div className="absolute top-[5%] right-[0%] w-[400px] h-[400px] rounded-full bg-orange-100/40 blur-[120px] dark:bg-orange-500/10 pointer-events-none" />
      <div className="absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full bg-amber-100/30 blur-[100px] dark:bg-amber-500/[0.08] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Left */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-500/[0.12] dark:border-orange-400/25 dark:text-orange-300 text-xs font-semibold tracking-widest uppercase mb-6">
              {lean.contact.badge}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight mb-6 dark:text-white">
              <Rich text={lean.contact.title} accentClassName="text-orange-600 dark:text-orange-500" />
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-8 max-w-md dark:text-white/60 mx-auto lg:mx-0">
              {lean.contact.text}
            </p>
            <div className="flex flex-col gap-4 text-stone-600 text-sm dark:text-white/60">
              {lean.contact.bullets.map((item) => (
                <div key={item.text} className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-white/[0.06]">
                    <i className={`${item.icon} text-stone-600 dark:text-white/60 text-sm`}></i>
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            {/* Contacts */}
            <div className="flex flex-col gap-3 text-stone-600 text-sm mt-8 dark:text-white/60">
              {site.contactsList.map((item) => (
                <div key={item.text} className="flex items-center gap-2 justify-center lg:justify-start">
                  <i className={`${item.icon} text-sm`}></i>
                  {item.href ? (
                    <a href={item.href} className="hover:text-stone-700 transition-colors cursor-pointer dark:hover:text-white">{item.text}</a>
                  ) : (
                    <span>{item.text}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Socials */}
            <div className="flex gap-3 mt-5 justify-center lg:justify-start">
              {site.socials.map((s) => (
                <a key={s.href} href={s.href} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-stone-600 hover:bg-stone-800 hover:text-white dark:bg-white/[0.06] dark:text-white/60 dark:hover:bg-orange-500 dark:hover:text-[#0C0C0E] transition-all cursor-pointer">
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white border border-stone-200 rounded-3xl p-8 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)] dark:bg-white/[0.04] dark:border-white/[0.09] dark:shadow-[0_4px_24px_rgba(0,0,0,0.2)]">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-2 uppercase tracking-wide dark:text-white/60">
                    {lean.contact.form.fields.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={lean.contact.form.fields.namePlaceholder}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:border-stone-400 dark:bg-white/[0.05] dark:border-white/[0.14] dark:text-white dark:placeholder-white/40 dark:focus:border-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-2 uppercase tracking-wide dark:text-white/60">
                    {lean.contact.form.fields.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder={lean.contact.form.fields.phonePlaceholder}
                    {...phoneMask}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:border-stone-400 dark:bg-white/[0.05] dark:border-white/[0.14] dark:text-white dark:placeholder-white/40 dark:focus:border-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-2 uppercase tracking-wide dark:text-white/60">
                    {lean.contact.form.fields.message}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder={lean.contact.form.fields.messagePlaceholder}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:border-stone-400 dark:bg-white/[0.05] dark:border-white/[0.14] dark:text-white dark:placeholder-white/40 dark:focus:border-white/30 transition-colors resize-none"
                  />
                  <div className="text-right text-stone-600 text-xs mt-1 dark:text-white/55">{charCount}/500</div>
                </div>
                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full py-4 rounded-2xl bg-orange-600 text-white font-bold text-sm hover:bg-orange-700 dark:bg-orange-500 dark:text-[#0A0A0A] dark:hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? lean.contact.form.submittingLabel : lean.contact.form.submitLabel}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </section>
  );
}